import React, { useContext, useEffect, useRef, useState } from "react";
import { CachedTokenContext } from "@seabrookstudios/auth";
import { GamePlayTestImagesCacheKey, useGamePlayTestPhotos, useMyEntitlements } from "@seabrookstudios/pitch2table";
import { s2Url } from "@seabrookstudios/types";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import endpoints from "../endpoints";
import { getImportUploadSkipMessage, shouldSkipImportUpload } from "./importUploadUtils";
import { hasExistingImportedPlaytestPhoto } from "./playtestImportUtils";
import { useUploadImage } from "../users/queries";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestId} props.playTestId
 * @param {File} props.file
 * @param {number} props.fileIndex
 * @param {boolean} props.hasLargeFileUploads
 * @param {() => void} props.onStart
 * @param {() => void} props.onComplete
 * @param {() => void} props.onSkip
 * @param {() => void} props.onError
 */
const UploadPlaytestPhoto = ({
  gameId,
  playTestId,
  file,
  fileIndex,
  hasLargeFileUploads,
  onStart,
  onComplete,
  onSkip,
  onError,
}) => {
  const { refetch } = useGamePlayTestPhotos(gameId, playTestId);
  const onStartRef = useRef(onStart);
  const onCompleteRef = useRef(onComplete);
  const onSkipRef = useRef(onSkip);
  const onErrorRef = useRef(onError);
  const refetchRef = useRef(refetch);
  onStartRef.current = onStart;
  onCompleteRef.current = onComplete;
  onSkipRef.current = onSkip;
  onErrorRef.current = onError;
  refetchRef.current = refetch;
  const token = useContext(CachedTokenContext);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const onFileUploadSuccess = async () => {
    enqueueSnackbar(`${file.name} uploaded`, { variant: "success" });
    await queryClient.invalidateQueries({ queryKey: GamePlayTestImagesCacheKey(gameId, playTestId) });
    onCompleteRef.current();
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occurred uploading play test photo", { variant: "error" });
    onErrorRef.current();
  };

  const { games } = endpoints();
  const url = games().playTestImages(gameId, playTestId);
  const uploadImage = useUploadImage(s2Url(url), token, "photo", onFileUploadSuccess, onFileUploadFailure);
  const uploadImageRef = useRef(uploadImage);
  uploadImageRef.current = uploadImage;

  useEffect(() => {
    let active = true;

    const uploadIfNeeded = async () => {
      if (shouldSkipImportUpload(file, hasLargeFileUploads)) {
        if (!active) {
          return;
        }

        enqueueSnackbar(getImportUploadSkipMessage(file), { variant: "warning" });
        onSkipRef.current();
        return;
      }

      try {
        const result = await refetchRef.current();

        if (!active) {
          return;
        }

        const photos = result.data || [];

        if (hasExistingImportedPlaytestPhoto(photos, file)) {
          onStartRef.current();
          onCompleteRef.current();
          return;
        }

        onStartRef.current();
        uploadImageRef.current(
          "photo",
          file,
          {},
          () => {},
          () => {},
          () => {},
          () => {}
        );
      } catch {
        if (!active) {
          return;
        }

        enqueueSnackbar("Could not check existing play test photos", { variant: "error" });
        onErrorRef.current();
      }
    };

    uploadIfNeeded();

    return () => {
      active = false;
    };
  }, [enqueueSnackbar, file, fileIndex, gameId, hasLargeFileUploads, playTestId]);

  return null;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestId} props.playTestId
 * @param {File[]} props.files
 * @param {(index: number) => void} props.onItemStart
 * @param {(index: number) => void} props.onItemComplete
 * @param {(index: number) => void} props.onItemSkip
 * @param {(index: number) => void} props.onItemError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
export const GameImportPlaytestPhotoUpload = ({
  gameId,
  playTestId,
  files,
  onItemStart,
  onItemComplete,
  onItemSkip,
  onItemError,
  onComplete,
  onError,
}) => {
  const { isFetched } = useGamePlayTestPhotos(gameId, playTestId);
  const { data: entitlements, isLoading: isEntitlementsLoading } = useMyEntitlements();
  const hasLargeFileUploads = Boolean(entitlements?.hasLargeFileUploads);
  const [fileIndex, setFileIndex] = useState(0);
  const hasCompletedBatch = useRef(false);
  const startedIndices = useRef(/** @type {Set<number>} */ (new Set()));
  const completedIndices = useRef(/** @type {Set<number>} */ (new Set()));
  const skippedIndices = useRef(/** @type {Set<number>} */ (new Set()));
  const onCompleteRef = useRef(onComplete);
  const onItemCompleteRef = useRef(onItemComplete);
  const onItemSkipRef = useRef(onItemSkip);
  onCompleteRef.current = onComplete;
  onItemCompleteRef.current = onItemComplete;
  onItemSkipRef.current = onItemSkip;
  const currentFile = files[fileIndex];

  const markItemComplete = (completedIndex) => {
    if (completedIndices.current.has(completedIndex)) {
      return;
    }

    completedIndices.current.add(completedIndex);
    onItemCompleteRef.current(completedIndex);
  };

  const finishBatch = () => {
    if (hasCompletedBatch.current) {
      return;
    }

    hasCompletedBatch.current = true;

    for (const startedIndex of startedIndices.current) {
      markItemComplete(startedIndex);
    }

    onCompleteRef.current();
  };

  const advanceFromIndex = (currentIndex) => {
    if (hasCompletedBatch.current) {
      return;
    }

    const nextIndex = currentIndex + 1;

    if (nextIndex >= files.length) {
      finishBatch();
      return;
    }

    setFileIndex(nextIndex);
  };

  useEffect(() => {
    if (fileIndex < files.length || hasCompletedBatch.current) {
      return;
    }

    finishBatch();
  }, [fileIndex, files.length, playTestId]);

  if (!isFetched || isEntitlementsLoading) {
    return null;
  }

  if (!currentFile) {
    return null;
  }

  const handleItemStart = (index) => {
    if (hasCompletedBatch.current || startedIndices.current.has(index)) {
      return;
    }

    startedIndices.current.add(index);
    onItemStart(index);
  };

  const handleItemComplete = (completedIndex) => {
    markItemComplete(completedIndex);
    advanceFromIndex(completedIndex);
  };

  const handleItemSkip = (skippedIndex) => {
    if (skippedIndices.current.has(skippedIndex)) {
      return;
    }

    skippedIndices.current.add(skippedIndex);
    onItemSkipRef.current(skippedIndex);
    advanceFromIndex(skippedIndex);
  };

  return (
    <UploadPlaytestPhoto
      key={fileIndex}
      gameId={gameId}
      playTestId={playTestId}
      file={currentFile}
      fileIndex={fileIndex}
      hasLargeFileUploads={hasLargeFileUploads}
      onStart={() => {
        handleItemStart(fileIndex);
      }}
      onComplete={() => {
        handleItemComplete(fileIndex);
      }}
      onSkip={() => {
        handleItemSkip(fileIndex);
      }}
      onError={() => {
        onItemError(fileIndex);
        onError();
      }}
    />
  );
};
